import { useQueries } from '@tanstack/react-query';
import { getAutomaticDiscountsV2, getStoreByIdV2, getCashierBoxesV2 } from '../services/store.service';
import { getSectionsV2 } from '../services/sections.service';

interface UseNewOrderQueriesParams {
  orderId?: string;
  storeId: string;
  order?: any;
}

interface UseNewOrderQueriesResult {
  storeDiscounts: {
    data: any;
    isLoading: boolean;
    error: any;
  };
  sections: {
    data: any;
    isLoading: boolean;
    error: any;
    refetch: () => void;
  };
  store: {
    data: any;
    isLoading: boolean;
    error: any;
  };
  cashierboxes: {
    data: any;
    isLoading: boolean;
    error: any;
  };
  isAnyLoading: boolean;
}

export const resolveAutomaticDiscountQueryContext = ({
  orderId,
  orderCreatedDate,
  now = () => new Date()
}: {
  orderId?: string;
  orderCreatedDate?: string;
  now?: () => Date;
}): {
  enabled: boolean;
  cacheDate: string;
  currentDate: string | null;
} => {
  if (!orderId) {
    return {
      enabled: true,
      cacheDate: 'current',
      currentDate: now().toISOString()
    };
  }

  if (!orderCreatedDate) {
    return {
      enabled: false,
      cacheDate: 'pending-order-date',
      currentDate: null
    };
  }

  const orderDate = new Date(orderCreatedDate);
  if (Number.isNaN(orderDate.getTime())) {
    return {
      enabled: false,
      cacheDate: 'pending-order-date',
      currentDate: null
    };
  }

  const currentDate = orderDate.toISOString();
  return {
    enabled: true,
    cacheDate: currentDate,
    currentDate
  };
};

export const useNewOrderQueries = ({
  orderId,
  storeId,
  order
}: UseNewOrderQueriesParams): UseNewOrderQueriesResult => {
  const automaticDiscountQueryContext = resolveAutomaticDiscountQueryContext({
    orderId,
    orderCreatedDate: order?.createdDate
  });
  const queries = useQueries({
    queries: [

      // Store discounts query
      {
        queryKey: ['automaticDiscounts', storeId, automaticDiscountQueryContext.cacheDate],
        enabled: !!storeId && automaticDiscountQueryContext.enabled,
        queryFn: async () => {
          try {
            const res = await getAutomaticDiscountsV2(storeId, {
              currentDate: automaticDiscountQueryContext.currentDate as string
            });
            if (!res?.data?.data?.discounts) {
              throw new Error('Oh no! Something went wrong while fetching.');
            }
            return res.data.data.discounts;
          } catch (e: any) {
            console.log('error caught', e['response']?.data?.['errors']);
            throw e;
          }
        }
      },
      // Sections query
      {
        queryKey: ['sections', { storeId, isActive: true, products: true }],
        enabled: !!storeId,
        queryFn: async () => {
          try {
            const res = await getSectionsV2({
              storeId,
              isActive: 'true',
              products: true
            });
            if (!res?.data?.data?.sections) {
              throw new Error('Oh no! Something went wrong while fetching.');
            }
            return res.data.data.sections;
          } catch (e: any) {
            console.log('error caught', e['response']?.data?.['errors']);
            throw e;
          }
        }
      },
      // Store query
      {
        queryKey: ['store', { _id: storeId }],
        enabled: !!storeId,
        queryFn: async () => {
          try {
            const res = await getStoreByIdV2(storeId);
            if (!res) {
              throw new Error('Oh no! Something went wrong while fetching.');
            }
            return res;
          } catch (e: any) {
            console.log('error caught', e['response']?.data?.['errors']);
            throw e;
          }
        }
      },
      // Cashierboxes query
      {
        queryKey: ['cashierbox', { storeId }],
        enabled: !!storeId,
        queryFn: async () => {
          try {
            const res = await getCashierBoxesV2(storeId);
            if (!res?.data?.data?.cashierBoxes) {
              throw new Error('Oh no! Something went wrong while fetching.');
            }
            return res.data.data.cashierBoxes;
          } catch (e: any) {
            console.log('error caught', e['response']?.data?.['errors']);
            throw e;
          }
        }
      }
    ]
  });

  const [storeDiscountsQuery, sectionsQuery, storeQuery, cashierboxesQuery] = queries;

  const isAnyLoading = queries.some(query => query.isLoading);

  return {
    storeDiscounts: {
      data: storeDiscountsQuery.data,
      isLoading: storeDiscountsQuery.isLoading,
      error: storeDiscountsQuery.error
    },
    sections: {
      data: sectionsQuery.data,
      isLoading: sectionsQuery.isLoading,
      error: sectionsQuery.error,
      refetch: sectionsQuery.refetch
    },
    store: {
      data: storeQuery.data,
      isLoading: storeQuery.isLoading,
      error: storeQuery.error
    },
    cashierboxes: {
      data: cashierboxesQuery.data,
      isLoading: cashierboxesQuery.isLoading,
      error: cashierboxesQuery.error
    },
    isAnyLoading
  };
};
